"use client";
import {
  GET_USER,
  GET_USERS,
  GET_REFERRAL_DRIVERS,
  GET_PROFILE_STATUS,
  UPDATE_USER,
  COMPLETE_DRIVER_PROFILE,
  VERIFY_DRIVER_AND_CONNECT_WITH_RIDER,
  COMPLETE_PROFILE,
  GET_RIDER_DETAILS,
  GET_USER_EARNINGS,
  GET_USER_DETAILS,
  GET_SERVICE_SUMMARY,
  UPDATE_PAYMENT_METHOD,
  VALIDATE_USER_BLOCKED,
} from "../end-points/user";
import {
  getFetcher,
  postFetcher,
  patchFetcher,
  verifyTokenPostFetcher,
  formDataPatchFetcher,
} from "../fetchers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Toast } from "@/component/Shared/Toast";
import { usePathname, useRouter } from "next/navigation";
import Cookies from "js-cookie";
import {
  MSA_TOKEN,
  USER_ID,
  USER_DATA,
  CREDIT_CARD,
  CASH_APP,
  PAYPAL,
} from "@/constants";
import { APPLICATION_ID } from "@/constants";
import { INSTALL_PROMPT } from "@/constants";
import { fbq } from "@/lib/fbPixels";
import { gtag } from "@/lib/gTag";
import { hashEmail } from "@/lib/hashEmail";

const useUserProfileStatus = () => {
  // const userId = Cookies.get(USER_ID);
  const rawId = Cookies.get(USER_ID);
  const userId =
    rawId && rawId !== "undefined" && rawId !== "null" ? rawId : null;

  const {
    isPending,
    isLoading,
    isFetching,
    error,
    data,
    isError,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: [GET_PROFILE_STATUS],
    queryFn: () => getFetcher(`${GET_PROFILE_STATUS}/${userId}`),
    //if set to true, API will be called the components in which it is used will be rendered
    refetchOnMount: true,
    // switching between chrome tabs
    refetchOnWindowFocus: false,
    // retry: 0, // Ensures the API is only retried once on failure
    enabled: !!userId,
  });

  if (data?.applicationId) {
    localStorage.setItem(APPLICATION_ID, data?.applicationId);
  }

  return {
    data: data?.user,
    isLoading: isPending,
    refetchLoader: isFetching,
    refetch,
    error,
    isSuccess,
    isError,
  };
};

const useCompleteProfile = () => {
  const router = useRouter();

  // const id = Cookies.get("id");
  const userId = Cookies.get(USER_ID);
  const applicationId = localStorage.getItem(APPLICATION_ID);

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [COMPLETE_PROFILE],
      mutationFn: patchFetcher,
      onSuccess: async (data) => {
        Toast.success(data?.message);
        Cookies.remove(USER_ID);
        localStorage.removeItem(USER_DATA);
        localStorage.removeItem(CREDIT_CARD);
        localStorage.removeItem(CASH_APP);
        localStorage.removeItem(PAYPAL);
        localStorage.removeItem("tasks");
        localStorage.removeItem(USER_DATA);
        localStorage.removeItem("workingHolidays");
        // localStorage.removeItem(APPLICATION_ID);

        Cookies.set(MSA_TOKEN, data?.token, { expires: 7 });

        const hashedEmail = await hashEmail(data?.email);
        // router.push(`/dashboard?applicationId=${applicationId}`);

        /***** FACEBOOK *****/
        fbq("trackCustom", "driver_completed_profile", {
          event_description: "Driver Completed Profile",
          role: "rider",
          email: hashedEmail,
          app_name: "Driver App",
        });
        /***** FACEBOOK *****/

        /***** GOOGLE *****/
        gtag("event", "driver_completed_profile", {
          event_description: "Driver Completed Profile",
          role: "rider",
          email: hashedEmail,
          app_name: "Driver App",
        });
        /***** GOOGLE *****/

        window.location.href = `/dashboard?applicationId=${applicationId}`;
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (args: any) =>
      mutate({ url: `${COMPLETE_PROFILE}/${userId}`, args: args }),
  };
};

const useUser = () => {
  const token = Cookies.get(MSA_TOKEN);

  const { isPending, isFetching, error, data, isError, isSuccess, refetch } =
    useQuery({
      queryKey: [GET_USER],
      queryFn: () => getFetcher(GET_USER),
      //if set to true, API will be called the components in which it is used will be rendered
      refetchOnMount: false,
      // switching between chrome tabs
      refetchOnWindowFocus: false,
      // he query will refetch on reconnect if the data is stale.
      refetchOnReconnect: false,
      retry: 0, // Ensures the API is only retried once on failure
      enabled: !!token, // Only run query if token exists
      staleTime: Infinity,
      gcTime: 5 * 60 * 1000,
      // staleTime: Infinity, // data never becomes stale
      // staleTime: 5 * 60 * 1000,
    });

  const userId = data?.user?._id;
  if (userId) {
    Cookies.set(USER_ID, userId, { expires: 7 });
  }

  if (data?.user?.applicationId?._id) {
    localStorage.setItem(APPLICATION_ID, data?.user?.applicationId?._id);
  }

  return {
    data: data?.user,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
    refetch,
  };
};

const useGetUsers = (query: string, userId: string) => {
  const { isPending, isFetching, error, data, isError, isSuccess, refetch } =
    useQuery({
      queryKey: [GET_USERS, query], // Add query to queryKey for proper caching
      queryFn: () => getFetcher(`${GET_USERS}?${query}`),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      // enabled: !!query, // Only run query if query string exists
      enabled: !!query && !!userId, // Only run query if query string exists
      retry: 0,
    });

  return {
    data: data?.data,
    isLoading: isPending || isFetching,
    error,
    refetch,
  };
};

const useGetReferralDrivers = (referrerDriverId: string) => {
  const { data, isLoading, isFetching, refetch, isSuccess, isError } = useQuery(
    {
      queryKey: [GET_REFERRAL_DRIVERS, referrerDriverId],
      queryFn: () =>
        getFetcher(
          `${GET_REFERRAL_DRIVERS}?${
            referrerDriverId
              ? // ? `referrerDriverId=${referrerDriverId}&isProfileCompleted=true`
                `referrerDriverId=${referrerDriverId}`
              : ""
          }`
        ),
      enabled: !!referrerDriverId, // Set this to false to disable this query from automatically running.
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );

  return {
    data: data?.data || [],
    isLoading: isLoading || isFetching,
    isError,
    refetch,
    isSuccess,
  };
};

const useGetUserById = (userId: string) => {
  const { isPending, error, data, isError, isSuccess } = useQuery({
    queryKey: [`get-user-${userId}`],
    queryFn: () => getFetcher(`${GET_RIDER_DETAILS}/${userId}`),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return {
    data: data?.user,
    isLoading: isPending,
    error,
    isError,
    isSuccess,
  };
};

const useUpdateUser = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Wait for user data to be available
  const { data: userData, isLoading: isUserLoading } = useUser();
  const userId = Cookies.get(USER_ID);

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [UPDATE_USER],
      mutationFn: formDataPatchFetcher,
      onSuccess: (data) => {
        Toast.success(data?.message);
        // Update the user data in the cache
        queryClient.setQueryData([GET_USER], (oldData: any) => ({
          ...oldData,
          user: data?.user,
        }));

        queryClient.setQueryData([GET_PROFILE_STATUS], (oldData: any) => ({
          ...oldData,
          user: {
            ...oldData?.user,
            userInformation: true,
          },
        }));
      },
      onError: (error) => {
        Toast.error(error.message);
      },
    });

  return {
    isSuccess,
    isError,
    isMutating: isPending || isUserLoading,
    error: error,
    data: data,
    reset: reset,
    trigger: (args: any) => {
      if (!userId) {
        Toast.error("Please wait while we load your user information");
        return;
      }
      return mutate({ url: `${UPDATE_USER}/${userId}`, args: args });
    },
  };
};

const useUpdateUserAccount = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  // Wait for user data to be available
  const { data: userData, isLoading: isUserLoading } = useUser();
  const userId = userData?._id;

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [UPDATE_USER],
      mutationFn: formDataPatchFetcher,
      onSuccess: (data) => {
        if (pathname === "/account") {
          // Don't show success toast on account page
        } else {
          Toast.success(data?.message);
        }
        // Update the user data in the cache
        queryClient.setQueryData([GET_USER], (oldData: any) => ({
          ...oldData,
          user: data?.user,
        }));
      },
      onError: (error) => {
        Toast.error(error.message);
      },
    });

  return {
    isSuccess,
    isError,
    isMutating: isPending || isUserLoading,
    error: error,
    data: data,
    reset: reset,
    trigger: (args: any) => {
      if (!userId) {
        Toast.error("Please wait while we load your user information");
        return;
      }
      return mutate({ url: `${UPDATE_USER}/${userId}`, args: args });
    },
  };
};

const useCompleteDriverProfile = () => {
  const router = useRouter();

  // const id = localStorage.getItem("id");
  const id = Cookies.get("id");

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [COMPLETE_DRIVER_PROFILE],
      mutationFn: patchFetcher,
      onSuccess: (data) => {
        // Toast.success(data?.message);
        Cookies.remove("id");
        // console.log("Data:", data);
        Cookies.set(MSA_TOKEN, data?.token, { expires: 7 });

        router.push("/dashboard");
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (args: any) =>
      mutate({ url: `${COMPLETE_DRIVER_PROFILE}/${id}`, args: args }),
  };
};

const useVerifyDriverAndConnectWithRider = () => {
  const queryClient = useQueryClient();

  const { isPending, isError, isSuccess, mutate, data, error, reset } =
    useMutation({
      mutationKey: [VERIFY_DRIVER_AND_CONNECT_WITH_RIDER],
      mutationFn: postFetcher,
      onSuccess: (data) => {
        queryClient.setQueryData([GET_USER], (oldData: any) => ({
          ...oldData,
          user: {
            ...oldData?.user,
            connectedDriverId: data?.driverId,
          },
        }));
      },
      onError: (error) => {
        Toast.error(error?.message);
      },
    });

  return {
    isError,
    isSuccess,
    isMutating: isPending,
    error: error,
    data: data,
    reset: reset,
    trigger: (args: any) =>
      mutate({ url: `${VERIFY_DRIVER_AND_CONNECT_WITH_RIDER}`, args: args }),
  };
};

const useGetUserDetailsById = (userId: string) => {
  const { isPending, isFetching, error, data, isError, isSuccess } = useQuery({
    queryKey: [`get-user-details-${userId}`],
    queryFn: () => getFetcher(`${GET_USER_DETAILS}/${userId}`),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!userId,
  });

  return {
    data: data?.data,
    isLoading: isPending || isFetching,
    error,
    isError,
    isSuccess,
  };
};

const useGetServiceSummary = (userId: string) => {
  const { isPending, error, data, isError, isSuccess } = useQuery({
    queryKey: [`get-service-summary-${userId}`],
    queryFn: () => getFetcher(`${GET_SERVICE_SUMMARY}?userId=${userId}`),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!userId,
  });

  return {
    data: data?.data,
    isLoading: isPending,
    error,
    isError,
    isSuccess,
  };
};

interface PaymentMethodData {
  paymentMethodId: string;
}

const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const router = useRouter();

  return useMutation({
    mutationFn: async (data: PaymentMethodData) => {
      const response = await patchFetcher({
        url: `${UPDATE_PAYMENT_METHOD}/${user?._id}`,
        args: {
          ...data,
          customerId: user?.paymentMethod?.customerId, // If exists, will update existing customer
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch user data to get updated payment method
      Toast.success(data?.message || "Payment method updated successfully");
      router.push("/billingAndPayment");
      queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
    onError: (error) => {
      Toast.error(error?.message || "Failed to update payment method");
    },
  });
};

const useInstallPrompt = () => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<any>([INSTALL_PROMPT]);

  return {
    deferredPrompt: data?.deferredPrompt ?? null,
  };
};

const useValidateUserBlocked = () => {
  return useMutation({
    mutationFn: () => getFetcher(`${VALIDATE_USER_BLOCKED}`),
    onSuccess: (data) => {
      console.log("data:", data);
    },
    onError: (error) => {
      console.log("error:", error);
      Toast.error(
        "Artificial Intelligence is unable to validate your information"
      );
    },
  });
};

export {
  useUserProfileStatus,
  useUser,
  useGetUsers,
  useGetReferralDrivers,
  useGetUserById,
  useUpdateUser,
  useUpdateUserAccount,
  useCompleteDriverProfile,
  useVerifyDriverAndConnectWithRider,
  useCompleteProfile,
  useGetUserDetailsById,
  useGetServiceSummary,
  useUpdatePaymentMethod,
  useInstallPrompt,
  useValidateUserBlocked,
};
